package com.patronat.top.ui.theme

import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.Font
import top.design_system.generated.resources.Res
import top.design_system.generated.resources.inter_bold
import top.design_system.generated.resources.inter_extra_bold
import top.design_system.generated.resources.inter_light
import top.design_system.generated.resources.inter_medium
import top.design_system.generated.resources.inter_regular
import top.design_system.generated.resources.inter_semibold

// ==========================================================================
// 1. SINGLE SOURCE OF TRUTH FOR FONTS (Cached)
// ==========================================================================
@Composable
fun rememberAppFontFamily(): FontFamily {
    val regular = Font(Res.font.inter_regular, FontWeight.Normal)
    val semiBold = Font(Res.font.inter_semibold, FontWeight.SemiBold)
    val bold = Font(Res.font.inter_bold, FontWeight.Bold)
    val extraBold = Font(Res.font.inter_extra_bold, FontWeight.ExtraBold)
    val medium = Font(Res.font.inter_medium, FontWeight.Medium)
    val light = Font(Res.font.inter_light, FontWeight.Light)

    // Це найважливіше місце: ми кешуємо FontFamily
    return remember(regular, semiBold, bold, medium, light) {
        FontFamily(regular, semiBold, bold, medium, light)
    }
}



val ExtraTitleLarge: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.ExtraBold,
        fontSize = 28.sp,
        lineHeight = 36.sp,
        color = MineShaft950
    )

val ExtraDescriptionLarge: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Bold,
        fontSize = 16.sp,
        lineHeight = 26.sp,
        color = MineShaft950
    )
val BodyLarge: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 16.sp,
        lineHeight = 24.sp,
        color = MineShaft900
    )

val LabelLarge: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.SemiBold,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        color = MineShaft900
    )

val BodyMedium: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Medium,
        fontSize = 14.sp,
        lineHeight = 16.sp,
        color = MineShaft500
    )



val InputTextContent: TextStyle
    @Composable get() = BodyLarge

val InputLabelText: TextStyle
    @Composable get() = LabelLarge

val VenueInputLabelText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.SemiBold,
        fontSize = 16.sp,
        lineHeight = 25.6.sp,
        letterSpacing = 0.sp,
        color = MineShaft900
    )

val VenueDescriptionText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 16.sp,
        lineHeight = 25.6.sp,
        letterSpacing = 0.sp,
        color = MineShaft900
    )

val VenueSectionTitleText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.SemiBold,
        fontSize = 18.sp,
        lineHeight = 25.2.sp,
        letterSpacing = 0.sp,
        color = MineShaft950
    )

val VenueScheduleText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 16.sp,
        lineHeight = 22.sp,
        letterSpacing = 0.sp,
        color = MineShaft900
    )

val VenueScheduleTimeInputText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        letterSpacing = 0.sp,
        color = MineShaft900
    )

val VenueScheduleDayOffText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 14.sp,
        lineHeight = 21.sp,
        letterSpacing = (-0.15).sp,
        color = MineShaft900
    )

val InputPlaceholderText: TextStyle
    @Composable get() = BodyLarge.copy(color = MineShaft500)

val RadioButtonText: TextStyle
    @Composable get() = BodyLarge.copy(fontWeight = FontWeight.Medium)

val TabTextStyle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Bold,
        fontSize = 16.sp,
        lineHeight = 20.sp
    )

val ButtonTextStyle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Medium,
        fontSize = 14.sp,
        lineHeight = 20.sp
    )

val AlertMessageText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        color = MineShaft500
    )

val OnboardingTitleText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.SemiBold,
        fontSize = 24.sp,
        lineHeight = 31.2.sp,
        letterSpacing = 0.24.sp,
        textAlign = TextAlign.Center,
        color = MineShaft950
    )

val OnboardingDescriptionText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 16.sp,
        lineHeight = 25.6.sp,
        letterSpacing = 0.2.sp,
        textAlign = TextAlign.Center,
        color = MineShaft600
    )

val OnboardingSkipText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 15.sp,
        lineHeight = 22.5.sp,
        letterSpacing = 0.sp,
        color = MineShaft950
    )

val HeroTitle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(), // <-- Кешований шрифт
        fontWeight = FontWeight.Bold,
        fontSize = 32.sp,
        lineHeight = 40.sp,
        color = White // Колір зафіксований, бо це стиль для темного фону
    )

val HeroSubtitle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 18.sp,
        lineHeight = 26.sp,
        color = White.copy(alpha = 0.9f)
    )

val SideMenuItemText: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Medium,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        color = MineShaft950
    )

// ... (твої існуючі стилі)

// --- TABLE STYLES ---

// 1. Заголовки колонок (маленькі, капсом, сірі)
val TableHeadTextStyle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Bold,
        fontSize = 11.sp,
        lineHeight = 16.sp,
        color = MineShaft500,
        letterSpacing = 0.5.sp // Трохи розрядимо літери для CAPS
    )

// 2. Звичайний текст у комірках (14sp, темний)
// На відміну від BodyMedium (який у тебе сірий), цей текст має бути темним для читабельності
val TableCellTextStyle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Normal,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        color = MineShaft900
    )

// 3. Текст для статусів (маленький, 12sp)
val BadgeTextStyle: TextStyle
    @Composable get() = TextStyle(
        fontFamily = rememberAppFontFamily(),
        fontWeight = FontWeight.Medium,
        fontSize = 12.sp,
        lineHeight = 16.sp,
        // Колір тут не задаємо, бо він залежить від фону бейджа
    )

// ==========================================================================
// 3. MATERIAL TYPOGRAPHY SETUP
// ==========================================================================
@Composable
fun rememberAppTypography(): Typography {
    val bodyLarge = BodyLarge
    val bodyMedium = BodyMedium
    val labelLarge = LabelLarge

    return remember(bodyLarge, bodyMedium, labelLarge) {
        Typography(
            bodyLarge = bodyLarge,
            bodyMedium = bodyMedium,
            labelLarge = labelLarge
        )
    }
}
